// @ts-check
/**
 * REGRA DE NEGÓCIO: PRESCRIÇÃO NO LIMBO (#2205)
 * =============================================
 *
 * Origem
 * ------
 * Ticket de suporte: um PAD previa "Téc. Enfermagem, Plantão 6h, 1x/dia" (30 visitas)
 * e o paciente usava medicações contínuas com horários fora do plantão,
 * administradas pela família. O agrupamento (`applyGroupEventsByTimeGapRules`,
 * regra de #2066) dividia o dia inteiro em blocos do tamanho do plantão a partir
 * de `shiftStartTime`, e cada bloco com evento virava uma visita: 4 blocos por dia
 * x 30 dias = 120 visitas exigidas para 30 contratadas, o que bloqueava a
 * inicialização sem alternativa na interface.
 *
 * Regra
 * -----
 * Nem toda aplicação de prescrição é coberta por uma visita. Em uma visita de
 * PLANTÃO, só as aplicações dentro da JANELA CONTRATADA geram visita. As demais
 * ficam no LIMBO:
 * - a aplicação continua existindo na prescrição e é gravada normalmente
 *   (permanece no payload `schedulePrescriptions` da inicialização);
 * - não existe visita para ela;
 * - não entra na contagem de visitas, no agrupamento nem na divisão entre agendas.
 *
 * Definições
 * - Visita de plantão: `padVisit` de Técnico de Enfermagem
 *   (`profession.CBOCode === CBOCODE.NURSING_TECHNICIAN`, 322205) com frequência
 *   DIÁRIA (`FREQUENCY_TYPE.DAY`). Frequências semanal, mensal etc. mantêm o
 *   comportamento anterior.
 * - Início do plantão: `shiftStartTime`, informado na etapa "Visualizar
 *   responsáveis pelas visitas" (#2145, #2195).
 * - Duração do plantão: `maxTimeDifferenceMinutes`, calculado pelo back-end
 *   (`visitationType.minimalLength`, senão `product.shiftDuration`). Para plantão
 *   sem duração configurada o back-end devolve `null` e `isShiftDurationMissing`
 *   (salvus-homecare-backend#3368); a etapa de responsáveis bloqueia o avanço e
 *   esta regra NUNCA é aplicada com duração nula (não há janela de 60 minutos
 *   para plantão).
 * - Janela contratada: blocos `0` até `frequencyAmount - 1` do ciclo diário
 *   iniciado em `shiftStartTime`, cada bloco com a duração do plantão e fim
 *   exclusivo (turnos consecutivos).
 *
 * Exemplo (plantão 6h a partir das 10h, 1x/dia, dose a cada 4h)
 *
 *   | Bloco          | Doses     | Resultado                    |
 *   | -------------- | --------- | ---------------------------- |
 *   | 10h às 16h     | 10h, 14h  | 1 visita (janela contratada) |
 *   | 16h às 22h     | 18h       | Limbo                        |
 *   | 22h às 04h     | 22h, 02h  | Limbo                        |
 *   | 04h às 10h     | 06h       | Limbo                        |
 *
 * Uma dose exatamente às 16h pertence ao bloco seguinte (fim exclusivo), logo vai
 * para o limbo. Plantão 12h 2x/dia cobre o dia inteiro e não gera limbo.
 *
 * `totalAmount` é limite rígido: excesso de visitas DENTRO da janela bloqueia o
 * avanço (modal "QUANTIDADE DE VISITAS EXCEDIDA") e não vai para o limbo.
 *
 * Decisão de arquitetura (aprovada pelo tech lead)
 * -------------------------------------------------
 * 1. Filtrar na origem: só eventos dentro da janela entram no agrupamento
 *    (`transformPrescriptionScheduleToLoadSchedule`,
 *    `transformPadPrescriptionsToPrescriptionLoadSchedule` e o selector de
 *    vínculo incompatível). Nenhum campo novo no store.
 * 2. O limbo é derivado por selector memoizado a partir de `schedulePrescriptions`
 *    e `padVisit`, reutilizando estas mesmas funções puras
 *    (`schedulePrescriptionInfoModal.selectors.js`).
 * 3. O vínculo de cada aplicação a uma visita é feito no back-end
 *    (salvus-homecare-backend#3368), que deve ir para produção antes ou junto.
 *
 * Os demais pontos do código apenas apontam para este comentário.
 */
import { differenceInMinutes, startOfDay, addMinutes, addDays } from 'date-fns';
import { CBOCODE } from 'app/config/profession';
import { FREQUENCY_TYPE } from 'app/config/padVisits';
import { toDateTimeISO, getShiftStartInMinutes } from './applyGroupEventsByTimeGapRules';

/** @typedef {import('./buildPadPrescriptionEvent').DtEvent} DtEvent */

/**
 * @typedef {Object} ShiftCoveragePadVisit
 * @property {{ CBOCode?: string|number }|null} [profession]
 * @property {number} [frequencyId]
 * @property {{ id?: number }|null} [frequencyType]
 * @property {number} [frequencyAmount]
 * @property {number|null} [maxTimeDifferenceMinutes]
 * @property {string|null} [shiftStartTime]
 */

/**
 * @typedef {Object} ShiftCoverageResult
 * @property {DtEvent[]} coveredEvents Eventos dentro da janela contratada (geram visita).
 * @property {DtEvent[]} limboEvents Eventos fora da janela contratada (sem visita).
 */

const MINUTES_PER_DAY = 24 * 60;

/**
 * Obtém o identificador da frequência da visita.
 * @param {ShiftCoveragePadVisit} padVisit Visita do PAD.
 * @returns {number|undefined} Identificador da frequência (`FREQUENCY_TYPE`).
 */
const getFrequencyId = padVisit => {
  if (padVisit.frequencyId) return padVisit.frequencyId;
  return padVisit.frequencyType ? padVisit.frequencyType.id : undefined;
};

/**
 * Indica se a regra de cobertura do plantão (limbo) se aplica à visita.
 * Exige Técnico de Enfermagem, frequência diária, início e duração do plantão.
 * @param {ShiftCoveragePadVisit|null|undefined} padVisit Visita do PAD.
 * @returns {boolean} `true` quando a regra se aplica.
 */
export const isShiftCoverageApplicable = padVisit => {
  if (!padVisit) return false;

  const { profession, frequencyAmount, maxTimeDifferenceMinutes, shiftStartTime } = padVisit;

  const isNursingTechnician =
    !!profession && String(profession.CBOCode) === String(CBOCODE.NURSING_TECHNICIAN);
  const isDailyFrequency = getFrequencyId(padVisit) === FREQUENCY_TYPE.DAY;
  const hasShiftDuration =
    typeof maxTimeDifferenceMinutes === 'number' && maxTimeDifferenceMinutes > 0;
  const hasFrequencyAmount = typeof frequencyAmount === 'number' && frequencyAmount > 0;

  return (
    isNursingTechnician &&
    isDailyFrequency &&
    hasShiftDuration &&
    hasFrequencyAmount &&
    !!shiftStartTime
  );
};

/**
 * Indica se um instante está dentro da janela contratada do plantão.
 * @param {Object} params Parâmetros.
 * @param {Date} params.dateTime Instante do evento.
 * @param {number} params.shiftStartInMinutes Início do plantão em minutos desde 00:00.
 * @param {number} params.maxTimeDifferenceMinutes Duração do plantão em minutos.
 * @param {number} params.frequencyAmount Quantidade de plantões consecutivos por dia.
 * @returns {boolean} `true` quando o instante está nos blocos `0..frequencyAmount - 1`.
 */
export const isWithinContractedShift = ({
  dateTime,
  shiftStartInMinutes,
  maxTimeDifferenceMinutes,
  frequencyAmount
}) => {
  const todayShiftStart = addMinutes(startOfDay(dateTime), shiftStartInMinutes);
  const cycleStart =
    dateTime.getTime() < todayShiftStart.getTime() ? addDays(todayShiftStart, -1) : todayShiftStart;

  const blockIndex = Math.floor(
    differenceInMinutes(dateTime, cycleStart) / maxTimeDifferenceMinutes
  );

  return blockIndex < frequencyAmount;
};

/**
 * Separa os eventos de prescrição entre os cobertos pela janela contratada do
 * plantão e os que ficam no limbo. Quando a regra não se aplica à visita, todos
 * os eventos são considerados cobertos (comportamento anterior).
 * @param {DtEvent[]} dateTimeEvents Eventos de prescrição da visita.
 * @param {ShiftCoveragePadVisit|null|undefined} padVisit Visita do PAD.
 * @returns {ShiftCoverageResult} Eventos cobertos e eventos no limbo.
 */
export const applyShiftCoverageRules = (dateTimeEvents, padVisit) => {
  const events = Array.isArray(dateTimeEvents) ? dateTimeEvents : [];

  if (!padVisit || !isShiftCoverageApplicable(padVisit)) {
    return { coveredEvents: events, limboEvents: [] };
  }

  const shiftStartInMinutes = getShiftStartInMinutes(padVisit.shiftStartTime || null);
  const maxTimeDifferenceMinutes = Number(padVisit.maxTimeDifferenceMinutes);
  const frequencyAmount = Number(padVisit.frequencyAmount);

  /** @type {DtEvent[]} */
  const coveredEvents = [];
  /** @type {DtEvent[]} */
  const limboEvents = [];

  events.forEach(dtEvent => {
    const dateTime = toDateTimeISO({ date: dtEvent.date, time: dtEvent.time });
    const isCovered = isWithinContractedShift({
      dateTime,
      shiftStartInMinutes,
      maxTimeDifferenceMinutes,
      frequencyAmount
    });

    if (isCovered) {
      coveredEvents.push(dtEvent);
    } else {
      limboEvents.push(dtEvent);
    }
  });

  return { coveredEvents, limboEvents };
};

/**
 * Formata minutos desde 00:00 como "HH:mm", com volta ao início após 24h.
 * @param {number} totalMinutes Minutos desde 00:00.
 * @returns {string} Horário no formato "HH:mm".
 */
const formatMinutesAsTime = totalMinutes => {
  const normalized = ((totalMinutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours = String(Math.floor(normalized / 60)).padStart(2, '0');
  const minutes = String(normalized % 60).padStart(2, '0');
  return `${hours}:${minutes}`;
};

/**
 * Retorna o rótulo da janela contratada do plantão (ex.: "das 10:00 às 16:00").
 * @param {ShiftCoveragePadVisit|null|undefined} padVisit Visita do PAD.
 * @returns {string|null} Rótulo da janela, ou `null` quando a regra não se aplica
 *   ou a janela cobre o dia inteiro.
 */
export const getContractedShiftWindowLabel = padVisit => {
  if (!padVisit || !isShiftCoverageApplicable(padVisit)) return null;

  const start = getShiftStartInMinutes(padVisit.shiftStartTime || null);
  const length = Number(padVisit.maxTimeDifferenceMinutes) * Number(padVisit.frequencyAmount);

  if (length >= MINUTES_PER_DAY) return null;

  return `das ${formatMinutesAsTime(start)} às ${formatMinutesAsTime(start + length)}`;
};

/**
 * Retorna a posição de um horário "HH:mm" no ciclo diário do plantão, para
 * ordenar horários a partir do início do plantão (ex.: 18:00, 22:00, 02:00, 06:00).
 * @param {string} time Horário no formato "HH:mm".
 * @param {string|null|undefined} shiftStartTime Início do plantão "HH:mm".
 * @returns {number} Minutos decorridos desde o início do plantão.
 */
export const getMinutesSinceShiftStart = (time, shiftStartTime) => {
  const timeInMinutes = getShiftStartInMinutes(time);
  const start = getShiftStartInMinutes(shiftStartTime || null);
  return (timeInMinutes - start + MINUTES_PER_DAY) % MINUTES_PER_DAY;
};
